import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

interface NavigationProps {
  scrolled: boolean;
}

export default function Navigation({ scrolled }: NavigationProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToSection = (id: string) => {
    // If not on home page, navigate to home first
    if (location.pathname !== '/') {
      navigate('/');
      // Wait for navigation to complete, then scroll
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      // Already on home page, just scroll
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setMobileMenuOpen(false);
  };

  const handleLogoClick = () => {
    navigate('/');
    setMobileMenuOpen(false);
  };

  const navigationItems = [
    { label: t('philosophy'), id: 'philosophy' },
    { label: t('services'), id: 'services' },
    { label: t('training'), id: 'training' },
    { label: t('credentials'), id: 'credentials' },
    { label: t('clientele'), id: 'clientele' },
    { label: t('approach'), id: 'approach' },
    { label: t('contact'), id: 'contact' }
  ];

  const menuItems = [
    { label: 'Home', href: '/' },
    { label: 'Services', href: '/services' },
    { label: 'Training', href: '/#training' },
    { label: 'Who We Serve', href: '/#who-we-serve' },
    { label: 'Contact', href: '/#contact' }
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled 
            ? 'bg-black/95 backdrop-blur-md border-b border-gray-800' 
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20 gap-4">
            <button 
              onClick={handleLogoClick}
              className="flex items-center gap-3 sm:gap-4 flex-shrink-0 cursor-pointer"
            >
              <img 
                src="https://static.readdy.ai/image/f8a1f6f2191eae46b1c6cd6b762cbc11/ccae452333f785a31e69f772b518f429.jpeg" 
                alt="U First Doctors" 
                className="h-10 sm:h-12 md:h-14 w-10 sm:w-12 md:w-14 object-contain"
              />
              <span className="text-[#F5F5F5] text-sm sm:text-base md:text-lg font-semibold whitespace-nowrap">U First Doctors</span>
            </button>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-6 xl:gap-8">
              {navigationItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-[10px] sm:text-[11px] uppercase tracking-[0.15em] text-[#A0A0A0] hover:text-[#F5F5F5] transition-colors cursor-pointer whitespace-nowrap"
                >
                  {item.label}
                </button>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden flex items-center gap-3 flex-shrink-0">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="flex items-center justify-center w-8 h-8 text-white"
              >
                <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`lg:hidden transition-all duration-300 overflow-hidden ${
          mobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className="bg-black/95 backdrop-blur-md border-t border-gray-800">
            <div className="px-4 py-4 space-y-4">
              {navigationItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="block w-full text-left text-sm uppercase tracking-wide text-[#A0A0A0] hover:text-[#F5F5F5] transition-colors py-2"
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </nav>
    </>
  );
}
